import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {GridColumnVisibilityModel} from '@mui/x-data-grid';
import InventoryService from '../shared/services/InventoryService';
import {flexeMuiTheme} from '../shared/mui/default-mui-theme';
import {FlexeDataGrid} from '../shared/mui/flexe-data-grid/FlexeDataGrid';
import {
  LocationContentsGridLocationContent,
  getLocationContentsColumns
} from '../shared/mui/flexe-data-grid/location-contents-grid/locationContentsGridColumns';
import LocationContentsGridHelpers from '../shared/mui/flexe-data-grid/location-contents-grid/LocationContentsGridHelpers';
import ItemMasterService from '../shared/services/ItemMasterService';

interface LpnContentSummaryTableProps {
  lpnId: number;
  baseUrl: string;
  inventoryService: InventoryService;
  itemMasterService: ItemMasterService;
}
export function LpnContentSummaryTable({
  lpnId,
  baseUrl,
  inventoryService,
  itemMasterService
}: LpnContentSummaryTableProps) {
  const [rows, setRows] = useState<LocationContentsGridLocationContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const columns = useMemo(() => getLocationContentsColumns(baseUrl), [baseUrl]);

  useEffect(() => {
    let isCancelled = false;

    async function fetchAllContents() {
      setIsLoading(true);
      try {
        const helpers = new LocationContentsGridHelpers(inventoryService, itemMasterService);
        const gridData = await helpers.fetchAllLpnContents(lpnId);

        if (isCancelled) {
          return;
        }

        setRows(gridData);
        setIsLoading(false);
      } catch (error) {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAllContents();

    return () => {
      isCancelled = true;
    };
  }, [lpnId, inventoryService, itemMasterService]);

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    const enableLpnsColumn = rows.some(({lpnBarcode}) => lpnBarcode);
    const enableUomColumn = rows.some(({uomJson}) => uomJson);
    const enableLotCodeColumn = rows.some(({lotCode}) => lotCode);
    const enableExpirationDateColumn = rows.some(({expirationDate}) => expirationDate);
    const enableManufactureDateColumn = rows.some(({manufactureDate}) => manufactureDate);

    // Hide these because they will always be the same for every row.
    const enableReservationIdColumn = false;
    const enableParentLpnBarcodeColumn = false;

    const model = {
      reservationId: enableReservationIdColumn,
      parentLpnBarcode: enableParentLpnBarcodeColumn,
      lpnBarcode: enableLpnsColumn,
      uomJson: enableUomColumn,
      lotCode: enableLotCodeColumn,
      expirationDate: enableExpirationDateColumn,
      manufactureDate: enableManufactureDateColumn
    };
    if (isLoading) {
      // Hide all columns until we know which ones to show
      for (const column of columns) {
        model[column.field] = false;
      }
    }
    return model;
  }, [columns, rows, isLoading]);

  return (
    <ThemeProvider theme={flexeMuiTheme}>
      <FlexeDataGrid
        columns={columns}
        rows={rows}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        isRowSelectable={() => false}
      />
    </ThemeProvider>
  );
}
